import { $, $$, setText } from './utils/index.js';
import { trackPageView } from './analytics.js';
import { onHomeActivated, renderHome } from './screens/home.js';
import { renderServices } from './screens/services.js';
import { renderServiceDetail } from './screens/service-detail.js';
import { renderOurWork } from './screens/our-work.js';
import { renderTechnologies } from './screens/technologies.js';
import { renderCareer } from './screens/career.js';
import { renderAbout } from './screens/about.js';
import { renderContact } from './screens/contact.js';
import { renderOutsourcingKosovo } from './screens/outsourcing-kosovo.js';
import { getSector, onSectorChange, type Sector } from './sector.js';
import { defaultSiteData } from './data/defaults.js';
import type { SiteData } from '../shared/types/site.js';
import { syncSeo } from './seo.js';

type NavPreview = {
  kicker: string;
  title: string;
  body?: string;
  items: Array<{ title: string; href: string; icon?: string }>;
  featured: Array<{ title: string; href: string; icon?: string }>;
};

const NAV_ICON_SVGS: Record<string, string> = {
  services:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3.5l7 4v9l-7 4-7-4v-9l7-4z" stroke="currentColor" stroke-width="1.8"/><path d="M12 8.2l3.6 2.1v3.4L12 15.8l-3.6-2.1v-3.4L12 8.2z" stroke="currentColor" stroke-width="1.5"/></svg>',
  technologies:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="2.8" stroke="currentColor" stroke-width="1.8"/><path d="M8.3 9.3l-2.3 2.7 2.3 2.7M15.7 9.3l2.3 2.7-2.3 2.7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  career:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4.5 9.2h15v9.8a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5V9.2z" stroke="currentColor" stroke-width="1.8"/><path d="M9 9.2V7.5A2.5 2.5 0 0 1 11.5 5h1A2.5 2.5 0 0 1 15 7.5v1.7" stroke="currentColor" stroke-width="1.8"/><path d="M4.5 12.2h15" stroke="currentColor" stroke-width="1.5"/></svg>',
  about:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8"/><path d="M12 10.1v5.2M12 7.8h.01" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>',
  contact:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5.5h14a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 17V7A1.5 1.5 0 0 1 5 5.5z" stroke="currentColor" stroke-width="1.8"/><path d="M4.2 7l7.2 5.4a1 1 0 0 0 1.2 0L19.8 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  web:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3.8" y="5" width="16.4" height="14" rx="2.4" stroke="currentColor" stroke-width="1.8"/><path d="M3.8 9h16.4M10 19V9M14 19V9" stroke="currentColor" stroke-width="1.4"/></svg>',
  mobile:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="7" y="3" width="10" height="18" rx="2.6" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="17.2" r="0.9" fill="currentColor"/></svg>',
  backend:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><ellipse cx="12" cy="6.5" rx="6.8" ry="2.8" stroke="currentColor" stroke-width="1.8"/><path d="M5.2 6.5v6c0 1.55 3.04 2.8 6.8 2.8s6.8-1.25 6.8-2.8v-6" stroke="currentColor" stroke-width="1.8"/><path d="M5.2 12.2v5.3c0 1.55 3.04 2.8 6.8 2.8s6.8-1.25 6.8-2.8v-5.3" stroke="currentColor" stroke-width="1.8"/></svg>',
  cloud:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7.6 18.2h8.3a4 4 0 0 0 .4-8 5.3 5.3 0 0 0-10 1.8 3.2 3.2 0 0 0 1.3 6.2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  devops:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8.3 7.3l2.2-2.2a2.8 2.8 0 0 1 3.9 0l4.5 4.5a2.8 2.8 0 0 1 0 3.9l-2.2 2.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M15.7 16.7l-2.2 2.2a2.8 2.8 0 0 1-3.9 0l-4.5-4.5a2.8 2.8 0 0 1 0-3.9l2.2-2.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M9.3 14.7l5.4-5.4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  consulting:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M10 14a4.6 4.6 0 1 1 3.3-7.8A4.6 4.6 0 0 1 10 14z" stroke="currentColor" stroke-width="1.8"/><path d="M13.5 13.5l4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  support:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7.5 9.2a4.5 4.5 0 1 1 9 0v2.9a2.7 2.7 0 0 1-2.7 2.7h-3.6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><rect x="4.4" y="11.2" width="2.8" height="5" rx="1.2" stroke="currentColor" stroke-width="1.6"/><rect x="16.8" y="11.2" width="2.8" height="5" rx="1.2" stroke="currentColor" stroke-width="1.6"/></svg>',
  ai:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="7.2" y="7.2" width="9.6" height="9.6" rx="2" stroke="currentColor" stroke-width="1.8"/><path d="M12 3.8v2.2M12 18v2.2M3.8 12h2.2M18 12h2.2M6.3 6.3l1.5 1.5M16.2 16.2l1.5 1.5M17.7 6.3l-1.5 1.5M7.8 16.2l-1.5 1.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
  design:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 19l7-7 3 3-7 7-3-3z" stroke="currentColor" stroke-width="1.6"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" stroke="currentColor" stroke-width="1.6"/></svg>',
  game:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 10h12a4 4 0 0 1 3.8 5.2l-1.1 3.3a2 2 0 0 1-3 1.1L15 18H9l-2.7 1.6a2 2 0 0 1-3-1.1l-1.1-3.3A4 4 0 0 1 6 10z" stroke="currentColor" stroke-width="1.6"/><path d="M8 14v2M7 15h2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="16.5" cy="14.5" r="1" fill="currentColor"/><circle cx="18.5" cy="16.5" r="1" fill="currentColor"/></svg>',
  product:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2.8l7.2 4.1v10.2L12 21.2 4.8 17.1V6.9L12 2.8z" stroke="currentColor" stroke-width="1.6"/><path d="M12 2.8v18.4M4.8 6.9L12 11l7.2-4.1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
  positions:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="4.2" y="6" width="15.6" height="12.2" rx="2.2" stroke="currentColor" stroke-width="1.8"/><path d="M8 6v-1a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1M4.2 10.5h15.6" stroke="currentColor" stroke-width="1.6"/></svg>',
  apply:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 4.8h8.6l3.4 3.4V19a1.4 1.4 0 0 1-1.4 1.4H6A1.4 1.4 0 0 1 4.6 19V6.2A1.4 1.4 0 0 1 6 4.8z" stroke="currentColor" stroke-width="1.8"/><path d="M14.6 4.8V8h3.2M8 12h8M8 15.5h6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
  clients:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="8.2" cy="9" r="2.2" stroke="currentColor" stroke-width="1.6"/><circle cx="15.8" cy="9" r="2.2" stroke="currentColor" stroke-width="1.6"/><path d="M4.8 17c.6-2 2.1-3 3.9-3h6.6c1.8 0 3.3 1 3.9 3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>',
  products:
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3.6l7.2 4.1v8.6L12 20.4 4.8 16.3V7.7L12 3.6z" stroke="currentColor" stroke-width="1.8"/><path d="M7.7 9.5L12 12l4.3-2.5M12 12v5.1" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
};

function renderPreviewIcon(icon?: string, cls = 'nav-preview-list-icon'): string {
  if (!icon) return '';
  const svg = NAV_ICON_SVGS[icon];
  if (!svg) return '';
  return `<span class="${cls}">${svg}</span>`;
}

const NAV_PREVIEWS: Record<Sector, Record<string, NavPreview>> = {
  tech: {
  home: {
    kicker: 'Studio Map',
    title: 'What Arkron Tech focuses on.',
    body: 'Client work across web, mobile, AI, and product delivery.',
    items: [
      { title: 'Services', href: '/services', icon: 'services' },
      { title: 'Technologies', href: '/technologies', icon: 'technologies' },
      { title: 'Career', href: '/career', icon: 'career' },
      { title: 'About Us', href: '/about-us', icon: 'about' },
    ],
    featured: [
      { title: 'Arkron Tech', href: '/services', icon: 'clients' },
      { title: 'Arkron Labs', href: '/our-work', icon: 'products' },
    ],
  },
  services: {
    kicker: 'Services',
    title: 'Core service tracks.',
    body: 'From architecture to production.',
    items: [
      { title: 'Consulting', href: '/services/consulting', icon: 'consulting' },
      { title: 'Maintenance & Support', href: '/services/maintenance-and-support', icon: 'support' },
      { title: 'AI Integration', href: '/services/ai-futuristic', icon: 'ai' },
      { title: 'Contact', href: '/contact-us', icon: 'contact' },
    ],
    featured: [
      { title: 'Web Development', href: '/services/web-development', icon: 'web' },
      { title: 'Mobile Development', href: '/services/mobile-app', icon: 'mobile' },
    ],
  },
  technologies: {
    kicker: 'Stack',
    title: 'Technology capabilities.',
    body: 'Selected for reliable scale.',
    items: [
      { title: 'Mobile', href: '/technologies#mobile', icon: 'mobile' },
      { title: 'Backend', href: '/technologies#backend', icon: 'backend' },
      { title: 'Cloud', href: '/technologies#cloud', icon: 'cloud' },
      { title: 'DevOps', href: '/technologies#devops', icon: 'devops' },
    ],
    featured: [],
  },
  career: {
    kicker: 'Team',
    title: 'Build meaningful products.',
    body: 'Quality-first, long-term growth.',
    items: [
      { title: 'How We Work', href: '/career', icon: 'career' },
      { title: 'Benefits', href: '/career', icon: 'services' },
      { title: 'Our Culture', href: '/about-us', icon: 'about' },
      { title: 'Contact', href: '/contact-us', icon: 'contact' },
    ],
    featured: [
      { title: 'Open Positions', href: '/career#open-positions', icon: 'positions' },
      { title: 'Apply', href: '/contact-us', icon: 'apply' },
    ],
  },
  about: {
    kicker: 'Company',
    title: 'Product technology studio.',
    body: 'Grounded in trust and execution.',
    items: [
      { title: 'Our Work', href: '/our-work', icon: 'products' },
      { title: 'Technologies', href: '/technologies', icon: 'technologies' },
      { title: 'Services', href: '/services', icon: 'services' },
      { title: 'Contact', href: '/contact-us', icon: 'contact' },
    ],
    featured: [],
  },
  },
  labs: {
    home: {
      kicker: 'Labs View',
      title: 'What Arkron Labs is building.',
      body: 'In-house products, yearly objectives, and systems we want to grow ourselves.',
      items: [
        { title: 'Walkthrough', href: '/services', icon: 'products' },
        { title: 'Technologies', href: '/technologies', icon: 'technologies' },
        { title: 'Career', href: '/career', icon: 'career' },
        { title: 'About Us', href: '/about-us', icon: 'about' },
      ],
      featured: [
        { title: 'Arkron Tech', href: '/', icon: 'clients' },
        { title: 'Arkron Labs', href: '/services', icon: 'products' },
      ],
    },
    services: {
      kicker: 'Walkthrough',
      title: 'How Arkron Labs is moving this year.',
      body: 'Objectives, progress, and what is being built next.',
      items: [
        { title: 'Current progress', href: '/services#current-progress', icon: 'products' },
        { title: 'Objectives', href: '/services#year-objectives', icon: 'services' },
        { title: 'Technologies', href: '/technologies', icon: 'technologies' },
        { title: 'Contact', href: '/contact-us', icon: 'contact' },
      ],
      featured: [
        { title: 'Labs roadmap', href: '/services#year-objectives', icon: 'products' },
        { title: 'What we ship', href: '/', icon: 'clients' },
      ],
    },
    technologies: {
      kicker: 'Stack',
      title: 'Technology capabilities.',
      body: 'The same engineering stack supports client work and internal products.',
      items: [
        { title: 'Mobile', href: '/technologies#mobile', icon: 'mobile' },
        { title: 'Backend', href: '/technologies#backend', icon: 'backend' },
        { title: 'Cloud', href: '/technologies#cloud', icon: 'cloud' },
        { title: 'DevOps', href: '/technologies#devops', icon: 'devops' },
      ],
      featured: [],
    },
    career: {
      kicker: 'Team',
      title: 'Build with us.',
      body: 'Same team, same standards, same long-term direction.',
      items: [
        { title: 'How We Work', href: '/career', icon: 'career' },
        { title: 'Benefits', href: '/career', icon: 'services' },
        { title: 'Our Culture', href: '/about-us', icon: 'about' },
        { title: 'Contact', href: '/contact-us', icon: 'contact' },
      ],
      featured: [
        { title: 'Open Positions', href: '/career#open-positions', icon: 'positions' },
        { title: 'Apply', href: '/contact-us', icon: 'apply' },
      ],
    },
    about: {
      kicker: 'Company',
      title: 'One studio, two tracks.',
      body: 'Client systems in Arkron Tech. Internal bets in Arkron Labs.',
      items: [
        { title: 'Walkthrough', href: '/services', icon: 'products' },
        { title: 'Technologies', href: '/technologies', icon: 'technologies' },
        { title: 'Career', href: '/career', icon: 'career' },
        { title: 'Contact', href: '/contact-us', icon: 'contact' },
      ],
      featured: [],
    },
  },
};

function normalizeServicePreviewTitle(title: string): string {
  switch (title) {
    case 'Mobile App':
      return 'Mobile App';
    case 'Creative Design (UI/UX)':
      return 'Creative Design';
    case 'Maintenance and Support':
      return 'Maintenance & Support';
    case 'AI futuristic':
      return 'AI Integration';
    case 'Original Products & Solutions':
      return 'Original Products';
    default:
      return title;
  }
}

function getNavPreview(key: string): NavPreview | null {
  const sector = getSector();
  if (key !== 'services') {
    return NAV_PREVIEWS[sector][key] ?? null;
  }

  if (sector !== 'tech') {
    return NAV_PREVIEWS[sector][key] ?? null;
  }

  const serviceSource = activeSiteData?.services?.length ? activeSiteData.services : defaultSiteData.services;
  const items = serviceSource.slice(0, 8).map((service) => ({
    title: normalizeServicePreviewTitle(service.title),
    href: `/services/${service.slug}`,
    icon: service.icon,
  }));

  return {
    kicker: 'Services',
    title: 'What we deliver.',
    items,
    featured: [],
  };
}

function getPath(): string {
  const p = window.location.pathname.replace(/\/$/, '') || '/';
  return p === '' ? '/' : p;
}

function decodeHash(rawHash: string): string {
  const cleaned = rawHash.replace(/^#/, '');
  if (!cleaned) return '';
  try {
    return decodeURIComponent(cleaned);
  } catch {
    return cleaned;
  }
}

function normalizeAnchor(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseRouteTarget(target: string): { path: string; hash: string } {
  try {
    const url = new URL(target || '/', window.location.origin);
    const path = (url.pathname.replace(/\/$/, '') || '/');
    return { path: path === '' ? '/' : path, hash: decodeHash(url.hash) };
  } catch {
    const [rawPath, rawHash = ''] = (target || '/').split('#');
    const path = (rawPath.replace(/\/$/, '') || '/');
    return { path: path === '' ? '/' : path, hash: decodeHash(rawHash) };
  }
}

function spotlightTarget(el: HTMLElement): void {
  el.classList.remove('route-spotlight');
  // Force reflow so repeated selections retrigger the animation.
  void el.offsetWidth;
  el.classList.add('route-spotlight');
  window.setTimeout(() => el.classList.remove('route-spotlight'), 1800);
}

function scrollToRouteAnchor(path: string, hash: string): boolean {
  if (!hash) return false;
  const pageId = 'page-' + (path === '/' ? 'home' : path.slice(1).replace(/\//g, '-'));
  const page = document.getElementById(pageId);
  if (!(page instanceof HTMLElement)) return false;

  const targetKey = normalizeAnchor(hash);
  if (!targetKey) return false;
  const target = Array.from(page.querySelectorAll<HTMLElement>('[data-route-anchor]'))
    .find((el) => normalizeAnchor(el.dataset.routeAnchor ?? '') === targetKey);
  if (!target) return false;

  const rootStyles = getComputedStyle(document.documentElement);
  const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height')) || 76;
  const top = window.scrollY + target.getBoundingClientRect().top - headerHeight - 20;
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  spotlightTarget(target);
  return true;
}

let scrollObserver: IntersectionObserver | null = null;
const TEXT_REVEAL_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'P', 'SPAN', 'A', 'LI']);

function initScrollAnimations(): void {
  const activePage = document.querySelector<HTMLElement>('.page.active');
  const animated = activePage
    ? Array.from(activePage.querySelectorAll<HTMLElement>('.animate-on-scroll'))
    : [];
  if (animated.length === 0) return;

  const supportsIO = 'IntersectionObserver' in window;
  animated.forEach((el, i) => {
    el.classList.remove('visible');
    el.classList.toggle('animate-text', TEXT_REVEAL_TAGS.has(el.tagName));
    const inlineDelay = el.style.animationDelay;
    if (inlineDelay) {
      const parsedDelay = Number.parseFloat(inlineDelay);
      const scaledDelay = Number.isFinite(parsedDelay)
        ? Math.max(0, parsedDelay * 0.55)
        : 0;
      el.style.setProperty('--reveal-delay', `${scaledDelay.toFixed(3)}s`);
    } else {
      const stagger = Math.min(i, 10) * 0.018;
      el.style.setProperty('--reveal-delay', `${stagger.toFixed(3)}s`);
    }
  });

  if (!supportsIO) {
    animated.forEach((el) => el.classList.add('visible'));
    return;
  }

  if (scrollObserver) {
    scrollObserver.disconnect();
    scrollObserver = null;
  }

  scrollObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          scrollObserver?.unobserve(e.target);
        }
      });
    },
    { rootMargin: '0px 0px 14% 0px', threshold: 0.01 }
  );

  const revealIfInView = (el: Element) => {
    const rect = el.getBoundingClientRect();
    if (rect.bottom > -40 && rect.top < window.innerHeight + 140) {
      el.classList.add('visible');
    }
  };

  animated.forEach((el) => {
    scrollObserver?.observe(el);
    revealIfInView(el);
  });

  window.setTimeout(() => {
    animated.forEach((el) => {
      if (!el.classList.contains('visible')) revealIfInView(el);
    });
    if (!document.querySelector('.animate-on-scroll.visible')) {
      animated.forEach((el) => el.classList.add('visible'));
    }
  }, 180);
}

function getServiceSlug(path: string): string | null {
  if (!path.startsWith('/services/')) return null;
  const parts = path.split('/').filter(Boolean);
  return parts.length >= 2 ? parts[1] : null;
}

const renderedPages = new Set<string>();

function renderPageForPath(path: string, siteData: SiteData): void {
  const serviceSlug = getServiceSlug(path);
  if (serviceSlug) {
    renderServiceDetail(siteData, serviceSlug);
    renderedPages.add('page-service-detail');
    return;
  }

  switch (path) {
    case '/':
      if (!renderedPages.has('page-home')) {
        renderHome(siteData);
        renderedPages.add('page-home');
      }
      break;
    case '/services':
      if (!renderedPages.has('page-services')) {
        renderServices(siteData);
        renderedPages.add('page-services');
      }
      break;
    case '/our-work':
      if (!renderedPages.has('page-our-work')) {
        renderOurWork(siteData);
        renderedPages.add('page-our-work');
      }
      break;
    case '/technologies':
      if (!renderedPages.has('page-technologies')) {
        renderTechnologies(siteData);
        renderedPages.add('page-technologies');
      }
      break;
    case '/career':
      if (!renderedPages.has('page-career')) {
        renderCareer();
        renderedPages.add('page-career');
      }
      break;
    case '/about-us':
      if (!renderedPages.has('page-about-us')) {
        renderAbout();
        renderedPages.add('page-about-us');
      }
      break;
    case '/contact-us':
      if (!renderedPages.has('page-contact-us')) {
        renderContact(siteData);
        renderedPages.add('page-contact-us');
      }
      break;
    case '/software-outsourcing-kosovo':
      if (!renderedPages.has('page-software-outsourcing-kosovo')) {
        renderOutsourcingKosovo();
        renderedPages.add('page-software-outsourcing-kosovo');
      }
      break;
    default:
      if (!renderedPages.has('page-home')) {
        renderHome(siteData);
        renderedPages.add('page-home');
      }
      break;
  }
}

export function showPage(path: string, siteData: SiteData, hash = ''): void {
  const serviceSlug = getServiceSlug(path);
  const pageId =
    serviceSlug ? 'page-service-detail' : 'page-' + (path === '/' ? 'home' : path.slice(1).replace(/\//g, '-'));
  renderPageForPath(path, siteData);
  syncSeo(path, siteData);
  $$('.page').forEach((p) => {
    const el = p as HTMLElement;
    el.hidden = p.id !== pageId;
    el.classList.toggle('active', p.id === pageId);
  });
  $$('.nav-links a').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    a.classList.toggle(
      'active',
      (path === '/' && href === '/') || (path !== '/' && (href === path || (href !== '/' && path.startsWith(href + '/'))))
    );
  });
  closeMobileNav();
  document.body.classList.remove('page-transition');
  requestAnimationFrame(() => {
    document.body.classList.add('page-transition');
    initScrollAnimations();
    const hasAnchorScroll = scrollToRouteAnchor(path, hash);
    if (!hasAnchorScroll) {
      scrollToTop();
    }
    if (path === '/') {
      onHomeActivated();
    }
    trackPageView(path, hash);
  });
}

let scrollResetTimer: number | null = null;

function scrollToTop(): void {
  if (scrollResetTimer) {
    window.clearTimeout(scrollResetTimer);
    scrollResetTimer = null;
  }

  const scroller = document.scrollingElement || document.documentElement;
  const html = document.documentElement;
  const body = document.body;
  const prevHtmlBehavior = html.style.scrollBehavior;
  const prevBodyBehavior = body.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  body.style.scrollBehavior = 'auto';

  let restored = false;
  const doScroll = () => {
    scroller.scrollTo({ top: 0, left: 0 });
    scroller.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0 });
  };

  const restore = () => {
    if (restored) return;
    restored = true;
    html.style.scrollBehavior = prevHtmlBehavior;
    body.style.scrollBehavior = prevBodyBehavior;
    window.removeEventListener('touchstart', restore);
    window.removeEventListener('wheel', restore);
  };

  doScroll();
  requestAnimationFrame(() => {
    doScroll();
    requestAnimationFrame(() => {
      doScroll();
      restore();
    });
  });

  window.addEventListener('touchstart', restore, { passive: true, once: true });
  window.addEventListener('wheel', restore, { passive: true, once: true });

  scrollResetTimer = window.setTimeout(() => {
    restore();
    scrollResetTimer = null;
  }, 80);
}

export function routeTo(path: string, siteData: SiteData): void {
  const parsed = parseRouteTarget(path ?? '/');
  const urlWithHash = parsed.hash ? `${parsed.path}#${encodeURIComponent(parsed.hash)}` : parsed.path;
  const currentUrlWithHash = `${window.location.pathname}${window.location.hash}`;
  if (urlWithHash !== currentUrlWithHash) {
    history.pushState({}, '', urlWithHash);
  }
  showPage(parsed.path, siteData, parsed.hash);
}

let navBound = false;
let navPreviewBound = false;
let mobileNavBound = false;
let activeSiteData: SiteData | null = null;

function setMobileNavOpen(next: boolean): void {
  const toggle = document.getElementById('nav-toggle');
  const panel = document.getElementById('nav-panel');
  if (!(toggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;
  panel.classList.toggle('is-open', next);
  toggle.setAttribute('aria-expanded', String(next));
  document.body.classList.toggle('nav-open', next);
}

function closeMobileNav(): void {
  setMobileNavOpen(false);
}

function syncSectorNavigation(): void {
  const sector = getSector();
  document.body.dataset.sector = sector;
  const servicesLinks = Array.from(document.querySelectorAll<HTMLElement>('[data-nav-role="services"]'));
  servicesLinks.forEach((link) => {
    link.textContent = sector === 'labs' ? 'Walkthrough' : 'Services';
    if (sector === 'labs') {
      link.removeAttribute('data-nav-preview');
    } else {
      link.setAttribute('data-nav-preview', 'services');
    }
  });
}

function bindMobileNav(): void {
  if (mobileNavBound) return;
  mobileNavBound = true;
  const toggle = document.getElementById('nav-toggle');
  const panel = document.getElementById('nav-panel');
  if (!(toggle instanceof HTMLButtonElement) || !(panel instanceof HTMLElement)) return;

  toggle.addEventListener('click', () => {
    const next = !panel.classList.contains('is-open');
    setMobileNavOpen(next);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeMobileNav();
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 1024) {
      closeMobileNav();
    }
  });

  window.addEventListener('pageshow', () => {
    closeMobileNav();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      closeMobileNav();
    }
  });

  closeMobileNav();
}

function bindNavPreview(): void {
  if (navPreviewBound) return;
  navPreviewBound = true;
  const wrap = document.getElementById('nav-preview-wrap');
  const wrapContainer = wrap?.querySelector<HTMLElement>('.container') ?? null;
  const kickerEl = document.getElementById('nav-preview-kicker');
  const titleEl = document.getElementById('nav-preview-title');
  const bodyEl = document.getElementById('nav-preview-body');
  const listEl = document.getElementById('nav-preview-list');
  const featuredEl = document.getElementById('nav-preview-featured');
  const header = document.querySelector('.header');
  const navLinks = Array.from(
    document.querySelectorAll<HTMLAnchorElement>('.nav-links a[data-nav-preview]')
  );
  const allNavLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('.nav-links a'));
  if (!wrap || !wrapContainer || !kickerEl || !titleEl || !bodyEl || !listEl || !featuredEl || !header || navLinks.length === 0) return;

  let closeTimer: number | null = null;
  const isDesktop = () => window.matchMedia('(min-width: 1025px)').matches;

  const clearCloseTimer = () => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
  };

  const closePreview = () => {
    clearCloseTimer();
    wrap.classList.remove('is-open');
    wrap.classList.remove('is-single');
    wrap.setAttribute('aria-hidden', 'true');
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer = window.setTimeout(closePreview, 140);
  };

  const openPreview = (key: string, trigger: HTMLAnchorElement) => {
    if (!isDesktop()) return;
    const preview = getNavPreview(key);
    if (!preview) return;
    const cards = preview.items;
    const previewWidth = 560;
    const containerRect = wrapContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const centeredLeft = triggerRect.left + triggerRect.width / 2 - containerRect.left - previewWidth / 2;
    const clampedLeft = Math.max(0, Math.min(centeredLeft, containerRect.width - previewWidth));
    kickerEl.textContent = preview.kicker;
    titleEl.textContent = preview.title;
    bodyEl.textContent = '';
    bodyEl.hidden = true;
    wrap.classList.remove('is-single');
    wrap.style.setProperty('--preview-width', `${previewWidth}px`);
    wrap.style.setProperty('--preview-left', `${clampedLeft}px`);
    listEl.innerHTML = cards
      .map(
        (item) =>
          `<a class="nav-preview-list-link" href="${item.href}">
            ${renderPreviewIcon(item.icon, 'nav-preview-feature-icon')}
            <span class="nav-preview-list-label">${item.title}</span>
          </a>`
      )
      .join('');
    featuredEl.innerHTML = '';
    wrap.classList.add('is-open');
    wrap.setAttribute('aria-hidden', 'false');
  };

  allNavLinks.forEach((link) => {
    link.addEventListener('mouseenter', () => {
      clearCloseTimer();
      const key = link.dataset.navPreview;
      if (!key) {
        closePreview();
        return;
      }
      openPreview(key, link);
    });
    link.addEventListener('focus', () => {
      const key = link.dataset.navPreview;
      if (!key) {
        closePreview();
        return;
      }
      openPreview(key, link);
    });
  });

  wrap.addEventListener('mouseenter', clearCloseTimer);
  wrap.addEventListener('mouseleave', scheduleClose);
  header.addEventListener('mouseleave', scheduleClose);
  header.addEventListener('click', (e) => {
    const target = e.target as Element | null;
    if (target?.closest('a')) closePreview();
  });
  header.addEventListener('focusout', (e) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !header.contains(next)) closePreview();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePreview();
  });
  window.addEventListener('resize', () => {
    if (!isDesktop()) closePreview();
  });
  document.addEventListener('click', (e) => {
    const target = e.target as Node | null;
    if (!target) return;
    if (!wrap.contains(target) && !header.contains(target)) closePreview();
  });
  onSectorChange(() => closePreview());
}

export function bindNav(siteData: SiteData): void {
  activeSiteData = siteData;
  if (navBound) return;
  navBound = true;
  syncSectorNavigation();
  const routeToWithData = (href: string) => {
    routeTo(href, activeSiteData ?? siteData);
  };
  document.addEventListener('click', (e) => {
    const target = e.target as Element | null;
    if (!target) return;
    const link = target.closest('a');
    if (!link) return;
    const href = link.getAttribute('href') ?? '';
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    if (link.getAttribute('target') && link.getAttribute('target') !== '_self') return;
    e.preventDefault();
    routeToWithData(href);
  });
  window.addEventListener('popstate', () => showPage(getPath(), activeSiteData ?? siteData, decodeHash(window.location.hash)));
  bindMobileNav();
  bindNavPreview();
  onSectorChange(() => {
    syncSectorNavigation();
  });
}

export function renderAll(siteData: SiteData): void {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
  activeSiteData = siteData;
  syncSectorNavigation();
  renderedPages.clear();
  setText('[data-footer-text]', siteData.footer?.text ?? '© 2026 ARKRON - Made with love by our team');
  routeTo(`${window.location.pathname}${window.location.hash}`, siteData);
  bindNav(siteData);
}
